import React from 'react';
import {
  Autocomplete,
  Checkbox, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid2, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField,
} from '@mui/material';
import { Field, useFormState } from 'react-final-form';
import validator from 'validator';
import { styled } from '@mui/material/styles';
// eslint-disable-next-line import/no-unresolved
import { MuiTelInput } from 'mui-tel-input';
// eslint-disable-next-line import/no-unresolved
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import get from 'lodash.get';

const requiredValidation = (value) => (value || value === 0 ? undefined : 'Erforderlich');
const emailValidation = (value) => (!value || validator.isEmail(value) ? undefined : 'Ungültige E-Mail Adresse');
const phoneValidation = (value) => {
  if (!value) return undefined;
  const normalizedValue = value.startsWith('+') ? value : `+${value}`;

  try {
    const phoneNumber = parsePhoneNumberFromString(normalizedValue);
    if (!phoneNumber || !phoneNumber.isValid()) {
      return 'Ungültige Telefonnummer';
    }
    return undefined;
  } catch (error) {
    return 'Ungültige Telefonnummer';
  }
};
const urlValidation = (value) => {
  if (!value) return undefined;
  try {
    // eslint-disable-next-line no-new
    new URL(value.startsWith('http') ? value : `https://${value}`);
    return undefined;
  } catch {
    return 'Ungültige URL';
  }
};
const composeValidators = (...validators) => (value) => validators.reduce((error, validator) => error || validator(value), undefined);

const evaluateDisableCondition = (condition, values, fieldName) => {
  if (!condition) return false;
  const { field, operator, value } = condition;
  const key = `${fieldName.split('.').slice(0, 2).join('.')}.${field}`;
  const formValue = get(values, key);

  switch (operator) {
    case 'equals':
      return formValue === value;
    case 'notEquals':
      return formValue !== value;
    case 'contains':
      return Array.isArray(formValue) && formValue.includes(value);
    case 'notContains':
      return Array.isArray(formValue) && !formValue.includes(value);
    default:
      return false;
  }
};

export const evaluateVisibility = (visibilityCondition, values, fieldName) => {
  if (!visibilityCondition) return true;

  const { field, operator, value } = visibilityCondition;
  const key = `${fieldName.split('.').slice(0, 2).join('.')}.${field}`;
  const formValue = get(values, key);

  switch (operator) {
    case 'equals':
      return formValue === value;
    case 'notEquals':
      return formValue !== value;
    case 'contains':
      return formValue?.includes(value);
    case 'notContains':
      return !formValue?.includes(value);
    default:
      return true;
  }
};

const SmallFlagTelInput = styled(MuiTelInput)({
  '& .MuiTelInput-Flag': {
    display: 'none',
  },
  '& .MuiInputAdornment-root': {
    display: 'none',
  },
  '& .MuiTelInput-FlagButton': {
    padding: '0 8px 0 4px',
  },
});

const countries = [
  { code: 'DE', name: 'Deutschland' },
  { code: 'AT', name: 'Österreich' },
  { code: 'CH', name: 'Schweiz' },
  { code: 'DK', name: 'Dänemark' },
  { code: 'PL', name: 'Polen' },
  { code: 'CZ', name: 'Tschechien' },
  { code: 'FR', name: 'Frankreich' },
  { code: 'LU', name: 'Luxemburg' },
  { code: 'BE', name: 'Belgien' },
  { code: 'NL', name: 'Niederlande' },
  { code: 'BG', name: 'Bulgarien' },
  { code: 'HR', name: 'Kroatien' },
  { code: 'CY', name: 'Zypern' },
  { code: 'EE', name: 'Estland' },
  { code: 'FI', name: 'Finnland' },
  { code: 'GR', name: 'Griechenland' },
  { code: 'HU', name: 'Ungarn' },
  { code: 'IE', name: 'Irland' },
  { code: 'IT', name: 'Italien' },
  { code: 'LV', name: 'Lettland' },
  { code: 'LT', name: 'Litauen' },
  { code: 'MT', name: 'Malta' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Rumänien' },
  { code: 'SK', name: 'Slowakei' },
  { code: 'SI', name: 'Slowenien' },
  { code: 'ES', name: 'Spanien' },
  { code: 'SE', name: 'Schweden' },
];

export default function InputField({
  name,
  label,
  disabled,
  gridSize = { xs: 12, lg: 6 },
  type = null,
  required = false,
  options = [],
  placeholder,
  noOptionsText,
  multiple = false,
  helperText,
  visibilityCondition = null,
  disableCondition = null,
}) {
  const formState = useFormState();
  const isDisabled = disabled || (disableCondition && evaluateDisableCondition(disableCondition, formState.values, name));

  const validators = [];
  if (required) validators.push(requiredValidation);
  if (type === 'email') validators.push(emailValidation);
  if (type === 'phone') validators.push(phoneValidation);
  if (type === 'url') validators.push(urlValidation);

  if (visibilityCondition && !evaluateVisibility(visibilityCondition, formState.values, name)) return null;

  if (type === 'checkbox') {
    const labelContent = required
      ? (typeof label === 'string' ? `${label} *` : label)
      : label;
    return (
      <Field
        name={name}
        type="checkbox"
        validate={composeValidators(...validators)}
        render={({ input, meta }) => (
          <Grid2 size={gridSize}>
            <FormControlLabel
              control={<Checkbox /* required={required} */ />}
              label={labelContent}
              disabled={isDisabled}
              sx={{ marginTop: '7px', marginLeft: 0, color: meta.touched && (meta.error || meta.submitError) ? '#d32f2f' : undefined }}
              {...input}
            />
          </Grid2>
        )}
      />
    );
  }

  if (type === 'radio') {
    const labelContent = required ? `${label} *` : label;
    return (
      <FormControl>
        {label && <FormLabel id="demo-radio-buttons-group-label">{labelContent}</FormLabel>}
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          name="radio-buttons-group"
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              control={(
                <Field
                  name={name}
                  type="radio"
                  render={({
                    input: {
                      name, value, onChange, checked, ...restInput
                    },
                  }) => (
                    <Radio
                      name={name}
                      value={value}
                      onChange={onChange}
                      checked={checked}
                      inputProps={restInput}
                      required={required}
                      disabled={isDisabled}
                    />
                  )}
                />
              )}
              label={option.name}
              value={option.value}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  }

  if (type === 'country') {
    const labelContent = required ? `${label} *` : label;
    return (
      <Field
        name={name}
        validate={composeValidators(...validators)}
        render={({ input, meta }) => (
          <Grid2 size={gridSize}>
            <FormControl fullWidth error={meta.touched && (meta.error || meta.submitError)}>
              <InputLabel id="demo-simple-select-label">{labelContent}</InputLabel>
              <Select
                label={labelContent}
                disabled={isDisabled}
                {...input}
              >
                {placeholder && <MenuItem disabled value=""><em>{placeholder}</em></MenuItem>}
                {countries.map((option) => (<MenuItem key={option.code} value={option.code}>{option.name}</MenuItem>))}
              </Select>
              {meta.error && meta.touched && <FormHelperText>{meta.error || meta.submitError}</FormHelperText>}
            </FormControl>
          </Grid2>
        )}
      />
    );
  }

  if (type === 'select') {
    const labelContent = required ? `${label} *` : label;
    return (
      <Field
        name={name}
        validate={composeValidators(...validators)}
        render={({ input, meta }) => {
          const optionsByValue = options?.reduce((acc, option) => {
            acc[option.value] = option;
            return acc;
          }, {});
          const value = multiple
            ? (Array.isArray(input.value) ? input.value : [])
            : (input.value ?? '');
          return (
            <Grid2 size={gridSize}>
              <FormControl fullWidth error={meta.touched && (meta.error || meta.submitError)}>
                <InputLabel id="demo-simple-select-label">{labelContent}</InputLabel>
                <Select
                  label={labelContent}
                  multiple={multiple}
                  MenuProps={{
                    disableScrollLock: true,
                  }}
                  /* required={required} */
                  {...input}
                  value={value}
                  disabled={isDisabled}
                  onChange={(event) => input.onChange(event.target.value)}
                  renderValue={multiple ? (selected) => (selected || []).map((val) => optionsByValue?.[val]?.name || val).join(', ') : undefined}
                >
                  {placeholder && !multiple && <MenuItem disabled value=""><em>{placeholder}</em></MenuItem>}
                  {options.map((option) => (
                    <MenuItem value={option.value}>
                      {multiple && (
                        <Checkbox
                          checked={Array.isArray(value) && value.includes(option.value)}
                          sx={{ mr: 1 }}
                        />
                      )}
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {meta.error && meta.touched && <FormHelperText>{meta.error || meta.submitError}</FormHelperText>}
              </FormControl>
            </Grid2>
          );
        }}
      />
    );
  }

  if (type === 'autocomplete') {
    const labelContent = required ? `${label} *` : label;
    return (
      <Field
        name={name}
        validate={composeValidators(...validators)}
        render={({ input, meta }) => {
          const selectedOptions = multiple
            ? (options
              .filter((option) => (input.value || []).includes(option.value))
              .map((option) => ({ label: option.name, value: option.value })))
            : null;
          let selectedOption = null;
          if (!multiple && input.value !== undefined) {
            selectedOption = options.find((option) => option.value === input.value);
            if (selectedOption) selectedOption = { label: selectedOption.name, value: selectedOption.value };
          }
          return (
            <Grid2 size={gridSize}>
              <Autocomplete
                required={required}
                multiple={multiple}
                fullWidth
                disabled={isDisabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={labelContent}
                    error={meta.touched && meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
                options={options.map((option) => ({ label: option.name, value: option.value }))}
                {...input}
                value={multiple ? selectedOptions : selectedOption}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                noOptionsText={noOptionsText}
                sx={{
                  '& .MuiAutocomplete-clearIndicator': {
                    visibility: 'visible !important',
                  },
                }}
                onChange={(_event, newValue) => {
                  if (multiple) {
                    input.onChange((newValue || []).map((item) => item.value));
                  } else {
                    input.onChange(newValue?.value);
                  }
                }}
              />
            </Grid2>
          );
        }}
      />
    );
  }

  if (type === 'phone') {
    return (
      <Field
        name={name}
        validate={composeValidators(...validators)}
        render={({ input, meta }) => (
          <Grid2 size={gridSize}>
            <SmallFlagTelInput
              fullWidth
              defaultCountry="DE"
              value={input.value}
              onChange={(val) => input.onChange(val === '' ? null : val)}
              label={`${label} ${required ? '*' : ''}`}
              variant="outlined"
              error={meta.touched && Boolean(meta.error || meta.submitError)}
              helperText={
                meta.touched && (meta.error || meta.submitError)
                  ? meta.error || meta.submitError
                  : helperText
              }
              disabled={isDisabled}
              placeholder="z. B. +49 123 4567890"
            />
          </Grid2>
        )}
      />
    );
  }

  if (type === 'url') {
    return (
      <Field
        name={name}
        validate={composeValidators(...validators)}
        render={({ input, meta }) => (
          <Grid2 size={gridSize}>
            <TextField
              error={meta.touched && (meta.error || meta.submitError)}
              label={`${label} ${required ? '*' : ''}`}
              variant="outlined"
              fullWidth
              disabled={isDisabled}
              placeholder={placeholder || 'https://...'}
              helperText={
                meta.touched && (meta.error || meta.submitError)
                  ? meta.error || meta.submitError
                  : helperText
              }
              {...input}
            />
          </Grid2>
        )}
      />
    );
  }

  return (
    <Field
      name={name}
      validate={composeValidators(...validators)}
      render={({ input, meta }) => (
        <Grid2 size={gridSize}>
          <TextField
            error={meta.touched && !meta.dirtySinceLastSubmit && (meta.error || meta.submitError)}
            type={type}
            label={`${label} ${required ? '*' : ''}`}
            variant="outlined"
            fullWidth
            disabled={isDisabled}
            placeholder={placeholder}
            helperText={
              meta.touched && !meta.dirtySinceLastSubmit
                ? (meta.error || meta.submitError || helperText)
                : helperText
            }
            {...input}
          />
        </Grid2>
      )}
    />
  );
}
